import { useEffect, type ComponentType, type ReactNode } from 'react';
import { useAnalyticsClient } from '@/analytics';
import { getDeviceId } from '@/lib/deviceId';
import { registerExperimentForDevtool } from './devtoolRegistry';
import { markExposedOnce } from './exposure';
import { CONTROL_VARIANT, EXCLUDED_VARIANT, useExperiment } from './useExperiment';

/**
 * Variant key → component. `control` is mandatory: it is the baseline arm,
 * the render for excluded users, and the bundled fallback when LaunchDarkly
 * is unreachable or serves an unknown variant.
 */
export type ExperienceRegistry = { control: ComponentType } & Record<string, ComponentType>;

export interface ExperienceSlotProps {
  /** The `exp.*` LaunchDarkly flag key driving this slot. */
  experiment: string;
  registry: ExperienceRegistry;
  /**
   * Rendered while the assignment is still resolving (LD initializing).
   * Use a layout-mirroring skeleton — rendering control here instead would
   * flash the wrong experience and contaminate the exposure.
   */
  fallback?: ReactNode;
}

/**
 * The generic experiment surface: reads the flag, renders the assigned
 * variant's component, and reports the exposure.
 *
 * On render of an assigned variant it (a) stamps the assignment as a
 * super-property (`{ [experiment]: variant }`) so every subsequent event can
 * be split by variant with zero extra instrumentation, and (b) fires
 * `experiment_started` once per (device, experiment, variant) — persisted
 * dedup, so reloads don't refire but a mid-experiment reassignment does.
 *
 * `__excluded` renders control and reports nothing: the user is not in the
 * experiment, and their events must not carry its super-prop.
 */
export function ExperienceSlot({ experiment, registry, fallback = null }: ExperienceSlotProps) {
  const { variant, ready, isFallback, isOverride } = useExperiment(experiment);

  const client = useAnalyticsClient();

  const excluded = variant === EXCLUDED_VARIANT;
  // Exposure reports what actually RENDERS: an unknown variant key (flag
  // ahead of the deployed bundle) falls back to control and is reported as such.
  const rendered = !excluded && registry[variant] ? variant : CONTROL_VARIANT;

  // Announce this slot to the experiments devtool (DEV builds only — the
  // condition is compile-time constant, so prod bundles drop the call).
  useEffect(() => {
    if (import.meta.env.DEV) registerExperimentForDevtool(experiment, Object.keys(registry));
  }, [experiment, registry]);

  useEffect(() => {
    // QA overrides render silently — no exposure, no super-prop.
    if (!ready || excluded || isOverride) return;
    // Re-stamped every mount (not just on first exposure): the super-prop is
    // session-scoped while the exposure dedup is persistent.
    client.register({ [experiment]: rendered });
    const unitId = getDeviceId();
    if (!markExposedOnce(experiment, rendered, unitId, isFallback)) return;
    client.track('experiment_started', {
      experiment_key: experiment,
      variant: rendered,
      unit_id: unitId,
      is_fallback: isFallback,
    });
  }, [ready, excluded, isOverride, rendered, experiment, client, isFallback]);

  if (!ready) return <>{fallback}</>;

  const Component = registry[rendered];
  return <Component />;
}
